//src/pages/inventory/Pharmacy/MedicineTransactionHistory.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  TablePagination,
  TextField,
  Stack,
  InputAdornment,
  Tooltip,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  MenuItem,
} from "@mui/material";
import { getAllMedicineTransactions, getMedicineTransactionsByPatientId } from "../../../services/medicineTransactionService";
import { getInvoiceList } from "../../../services/invoiceService";
import { getAllSuppliers } from "../../../services/supplierService";
import Header from "../../../components/Header";
import dayjs from "dayjs";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DownloadIcon from "@mui/icons-material/Download";
import Refresh from "@mui/icons-material/Refresh";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import {
  TABLE_ICON_BUTTON_SX,
  TABLE_ICON_SX,
  TABLE_SEARCH_ROW_SX,
} from "../../../utils/pageLayoutStyles";
import { addOrgToUrl } from "../../../utils/organizationUtils";
import { buildPatientTimelineNavigation } from "../../../utils/patientTimelineVisitUtils";
import { downloadStyledTableExcel } from "../../../utils/patientTableExcelExport";

/** Coerce supplier-related API values (string / number / nested object) to a display name. */
const resolveSupplierDisplayName = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }
  if (typeof value === "object") {
    return String(
      value.name ||
        value.supplierName ||
        value.fromName ||
        value.vendorName ||
        value.label ||
        ""
    ).trim();
  }
  return "";
};

const getTransactionSupplierName = (transaction) =>
  resolveSupplierDisplayName(transaction?.supplierName) ||
  resolveSupplierDisplayName(transaction?.supplier) ||
  resolveSupplierDisplayName(transaction?.fromName) ||
  resolveSupplierDisplayName(transaction?.vendorName) ||
  resolveSupplierDisplayName(transaction?.vendor) ||
  "";

const getTransactionInvoiceId = (transaction) =>
  transaction?.invoiceId ??
  transaction?.invoice_id ??
  transaction?.invoice?.id ??
  null;

const getTransactionPrescriptionId = (transaction) =>
  transaction?.prescriptionId ??
  transaction?.prescription_id ??
  transaction?.prescription?.id ??
  null;

const getTransactionPatientId = (transaction) =>
  transaction?.patientId ??
  transaction?.patient_id ??
  transaction?.patient?.id ??
  null;

const clickableIdSx = {
  color: "#568AF7",
  cursor: "pointer",
  textDecoration: "none",
  fontWeight: 500,
};

const getTransactionSupplierId = (transaction) =>
  transaction?.supplierId ??
  transaction?.supplier_id ??
  transaction?.supplier?.id ??
  null;

const columns = [
  // { id: "id", label: "Transaction ID" },
  
  { id: "medicineName", label: "Medicine Name" },
 
  { id: "batchNumber", label: "Batch Number" },
  // { id: "manufacturer", label: "Manufacturer" },
  { id: "medicineType", label: "Medicine Type" },
  // { id: "initialQuantity", label: "Initial Qty" },
  { id: "deductedQuantity", label: "Deducted/Added Qty" },
  { id: "transactionType", label: "Transaction Type" },
  { id: "transactionDate", label: "Transaction Date" },
  { id: "patientName", label: "Patient" },
  // { id: "patientType", label: "Patient Type" },
  { id: "doctorName", label: "Doctor" },
  { id: "supplierName", label: "Supplier Name" },
  { id: "invoiceId", label: "Invoice ID" },
  { id: "prescriptionId", label: "Prescription ID" },
  { id: "costPerUnit", label: "Cost/Unit" },
  // { id: "totalCost", label: "Total Cost" },
];

const MedicineTransactionHistory = ({ onViewInventoryBatch } = {}) => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterMedicine, setFilterMedicine] = useState("");
  const [filterBatch, setFilterBatch] = useState("");
  const [filterManufacturer, setFilterManufacturer] = useState("");
  const [filterMedicineType, setFilterMedicineType] = useState("");
  const [filterPatient, setFilterPatient] = useState("");
  const [filterPatientType, setFilterPatientType] = useState("");
  const [filterDoctor, setFilterDoctor] = useState("");
  const [filterSupplier, setFilterSupplier] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchTransactions();
  }, [patientId]);

  useEffect(() => {
    filterTransactions();
  }, [transactions, searchQuery, filterType, filterMedicine, filterBatch, filterManufacturer, filterMedicineType, filterPatient, filterPatientType, filterDoctor, filterSupplier, fromDate, toDate]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      let data;
      if (patientId) {
        data = await getMedicineTransactionsByPatientId(patientId);
      } else {
        data = await getAllMedicineTransactions();
      }

      // Enrich with supplier name from linked invoices / suppliers when not present on transaction
      let invoiceById = new Map();
      let supplierById = new Map();
      try {
        const [invoices, suppliers] = await Promise.all([
          getInvoiceList({ fullRange: true }).catch((err) => {
            console.warn(
              "[MedicineTransactionHistory] Could not load invoices for supplier names:",
              err
            );
            return [];
          }),
          getAllSuppliers().catch((err) => {
            console.warn(
              "[MedicineTransactionHistory] Could not load suppliers for supplier names:",
              err
            );
            return [];
          }),
        ]);

        const invoiceList = Array.isArray(invoices)
          ? invoices
          : Array.isArray(invoices?.data)
            ? invoices.data
            : Array.isArray(invoices?.content)
              ? invoices.content
              : [];
        invoiceById = new Map(
          invoiceList
            .filter((inv) => inv?.id != null)
            .map((inv) => [String(inv.id), inv])
        );

        const supplierList = Array.isArray(suppliers) ? suppliers : [];
        supplierById = new Map(
          supplierList
            .filter((s) => s?.id != null)
            .map((s) => [
              String(s.id),
              resolveSupplierDisplayName(s) ||
                resolveSupplierDisplayName(s.name) ||
                resolveSupplierDisplayName(s.supplierName),
            ])
        );
      } catch (enrichError) {
        console.warn(
          "[MedicineTransactionHistory] Supplier enrichment skipped:",
          enrichError
        );
      }

      const enriched = (data || []).map((tx) => {
        const invoiceId = getTransactionInvoiceId(tx);
        const invoice =
          invoiceId != null ? invoiceById.get(String(invoiceId)) : null;

        const existingPrescriptionId = getTransactionPrescriptionId(tx);
        const prescriptionFromInvoice =
          invoice?.prescriptionId ?? invoice?.prescription_id ?? null;
        const prescriptionId =
          existingPrescriptionId ?? prescriptionFromInvoice ?? null;
        const patientIdFromTx = getTransactionPatientId(tx);
        const patientIdFromInvoice =
          invoice?.patientId ?? invoice?.patient_id ?? invoice?.patient?.id ?? null;
        const resolvedPatientId = patientIdFromTx ?? patientIdFromInvoice ?? null;
        const resolvedPatientType =
          tx.patientType ||
          tx.patient_type ||
          ((invoice?.admissionId || invoice?.admission_id) ? "ipd" : "");

        const existing = getTransactionSupplierName(tx);
        if (existing) {
          return {
            ...tx,
            invoiceId: invoiceId ?? tx.invoiceId ?? tx.invoice_id ?? null,
            prescriptionId,
            prescription_id: prescriptionId,
            patientId: resolvedPatientId,
            patient_id: resolvedPatientId,
            patientType: resolvedPatientType || tx.patientType,
            supplierName: existing,
          };
        }

        const isStockIn =
          String(tx.transactionType || "").toUpperCase() === "IN";
        const fromInvoice =
          resolveSupplierDisplayName(invoice?.supplierName) ||
          resolveSupplierDisplayName(invoice?.supplier) ||
          (isStockIn
            ? resolveSupplierDisplayName(invoice?.fromName) ||
              resolveSupplierDisplayName(invoice?.from_name)
            : "") ||
          "";

        const supplierId =
          getTransactionSupplierId(tx) ??
          invoice?.supplierId ??
          invoice?.supplier_id ??
          invoice?.supplier?.id ??
          null;
        const fromSupplier =
          supplierId != null ? supplierById.get(String(supplierId)) || "" : "";

        return {
          ...tx,
          invoiceId: invoiceId ?? tx.invoiceId ?? tx.invoice_id ?? null,
          prescriptionId,
          prescription_id: prescriptionId,
          patientId: resolvedPatientId,
          patient_id: resolvedPatientId,
          patientType: resolvedPatientType || tx.patientType,
          supplierName: fromInvoice || fromSupplier || "",
        };
      });
      
      // Sort by transaction date (newest first)
      const sorted = [...enriched].sort((a, b) => 
        new Date(b.transactionDate) - new Date(a.transactionDate)
      );
      
      setTransactions(sorted);
    } catch (error) {
      console.error("Failed to fetch transactions", error);
    }
    setLoading(false);
  };

  const filterTransactions = () => {
    let filtered = [...transactions];

    // Global search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((transaction) => {
        const values = [
          transaction.id,
          transaction.medicineName,
          transaction.medicineInventoryId,
          transaction.batchNumber,
          transaction.manufacturer,
          transaction.medicineType,
          transaction.patientName,
          transaction.patientType,
          transaction.doctorName,
          getTransactionSupplierName(transaction),
          transaction.invoiceId,
          getTransactionPrescriptionId(transaction),
        ]
          .filter(Boolean)
          .map((v) => String(v).toLowerCase());
        return values.some((v) => v.includes(q));
      });
    }

    // Transaction Type filter
    if (filterType) {
      filtered = filtered.filter((transaction) => 
        String(transaction.transactionType || "") === filterType
      );
    }

    // Medicine Name filter
    if (filterMedicine.trim()) {
      const q = filterMedicine.toLowerCase();
      filtered = filtered.filter((transaction) =>
        String(transaction.medicineName || "").toLowerCase().includes(q)
      );
    }

    // Batch Number filter
    if (filterBatch.trim()) {
      const q = filterBatch.toLowerCase();
      filtered = filtered.filter((transaction) =>
        String(transaction.batchNumber || "").toLowerCase().includes(q)
      );
    }

    // Manufacturer filter
    if (filterManufacturer) {
      filtered = filtered.filter((transaction) =>
        String(transaction.manufacturer || "") === filterManufacturer
      );
    }

    // Medicine Type filter
    if (filterMedicineType) {
      filtered = filtered.filter((transaction) =>
        String(transaction.medicineType || "") === filterMedicineType
      );
    }

    // Patient Name filter
    if (filterPatient.trim()) {
      const q = filterPatient.toLowerCase();
      filtered = filtered.filter((transaction) =>
        String(transaction.patientName || "").toLowerCase().includes(q)
      );
    }

    // Patient Type filter
    if (filterPatientType) {
      filtered = filtered.filter((transaction) =>
        String(transaction.patientType || "").toLowerCase() === filterPatientType.toLowerCase()
      );
    }

    // Doctor Name filter
    if (filterDoctor.trim()) {
      const q = filterDoctor.toLowerCase();
      filtered = filtered.filter((transaction) =>
        String(transaction.doctorName || "").toLowerCase().includes(q)
      );
    }

    // Supplier Name filter
    if (filterSupplier) {
      filtered = filtered.filter(
        (transaction) =>
          String(getTransactionSupplierName(transaction) || "") === filterSupplier
      );
    }

    // Date range filter
    if (fromDate || toDate) {
      filtered = filtered.filter((transaction) => {
        const d = transaction.transactionDate ? new Date(transaction.transactionDate) : null;
        if (!d) return false;
        if (fromDate && d < new Date(fromDate)) return false;
        if (toDate) {
          const toEnd = new Date(toDate);
          toEnd.setHours(23, 59, 59, 999);
          if (d > toEnd) return false;
        }
        return true;
      });
    }

    setFilteredTransactions(filtered);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleMedicineNameClick = (transaction) => {
    const medicineName = String(
      transaction?.medicineName || transaction?.name || transaction?.medicine || ""
    ).trim();
    if (!medicineName) return;
    if (typeof onViewInventoryBatch === "function") {
      onViewInventoryBatch({
        medicineName,
        batchNumber:
          transaction?.batchNumber ||
          transaction?.batch_number ||
          transaction?.batchNo ||
          "",
        medicineId: transaction?.medicineId ?? transaction?.medicine_id ?? null,
        medicineInventoryId:
          transaction?.medicineInventoryId ??
          transaction?.medicine_inventory_id ??
          transaction?.inventoryId ??
          null,
      });
      return;
    }
    navigate(addOrgToUrl("/inventory?tab=medicine", user));
  };

  const handleInvoiceIdClick = (transaction) => {
    const invoiceId = getTransactionInvoiceId(transaction);
    if (invoiceId == null || invoiceId === "") {
      toast.info("Invoice ID is not available for this transaction");
      return;
    }
    navigate(
      addOrgToUrl(`/invoice?invoiceId=${encodeURIComponent(invoiceId)}`, user)
    );
  };

  const handlePrescriptionIdClick = (transaction) => {
    const prescriptionId = getTransactionPrescriptionId(transaction);
    if (prescriptionId == null || prescriptionId === "") {
      toast.info("Prescription ID is not available for this transaction");
      return;
    }
    const linkedPatientId = getTransactionPatientId(transaction);
    if (linkedPatientId == null || linkedPatientId === "") {
      toast.info("Patient is not linked for this prescription");
      return;
    }
    const isIpd =
      String(transaction?.patientType || "").trim().toLowerCase() === "ipd";
    const { path, state } = buildPatientTimelineNavigation({
      user,
      patientId: linkedPatientId,
      isIpd,
      timelineTab: "prescriptions",
    });
    navigate(addOrgToUrl(path, user), state ? { state } : undefined);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setFilterType("");
    setFilterMedicine("");
    setFilterBatch("");
    setFilterManufacturer("");
    setFilterMedicineType("");
    setFilterPatient("");
    setFilterPatientType("");
    setFilterDoctor("");
    setFilterSupplier("");
    setFromDate("");
    setToDate("");
  };

  const handleExport = async () => {
    const rows = filteredTransactions || [];
    const headers = [
      "Transaction ID", "Medicine Name", "Inventory ID", "Batch Number", "Manufacturer", "Type",
      "Initial Qty", "Deducted Qty", "Final Qty", "Expiry Date", "Transaction Type",
      "Transaction Date", "Patient", "Patient Type", "Doctor", "Supplier Name", "Invoice ID",
      "Prescription ID",
    ];
    const tableRows = rows.map((r) => [
      r.id,
      r.medicineName,
      r.medicineInventoryId,
      r.batchNumber,
      r.manufacturer,
      r.medicineType,
      r.initialQuantity,
      r.transactionType === "IN" && r.deductedQuantity < 0 ? Math.abs(r.deductedQuantity) : r.deductedQuantity,
      r.finalQuantity,
      r.expiryDate ? dayjs(r.expiryDate).format("D MMM YYYY") : "N/A",
      r.transactionType,
      r.transactionDate ? dayjs(r.transactionDate).format("D MMM YYYY HH:mm") : "N/A",
      r.patientName,
      r.patientType || "N/A",
      r.doctorName || "N/A",
      getTransactionSupplierName(r) || "N/A",
      r.invoiceId,
      getTransactionPrescriptionId(r) || "N/A",
    ]);

    await downloadStyledTableExcel({
      filename: `medicine_transaction_history_${dayjs().format("YYYY-MM-DD")}`,
      sheetName: "Medicine Transactions",
      title: "Medicine Transaction History",
      tableHeaders: headers,
      tableRows,
    });
  };
  console.log("transactions",transactions);
  

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100%',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column'
    }}>
        <Box sx={{ p: 1.5, pt: 1, flexShrink: 0, overflow: "visible" }}>
          <Box sx={TABLE_SEARCH_ROW_SX}>
            <TextField
              placeholder="Search Transactions"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={TABLE_ICON_SX} fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title={showFilters ? "Hide filters" : "Show filters"}>
              <IconButton
                size="small"
                sx={TABLE_ICON_BUTTON_SX}
                onClick={() => setShowFilters((v) => !v)}
              >
                <FilterAltIcon
                  fontSize="small"
                  color={showFilters ? "primary" : "inherit"}
                />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export to Excel">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={handleExport}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton
                size="small"
                sx={TABLE_ICON_BUTTON_SX}
                onClick={async () => await fetchTransactions()}
                disabled={loading}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {showFilters && (
            <Box sx={{ 
              mt: 1, 
              p: 2, 
              bgcolor: '#f8f9fa', 
              borderRadius: 2, 
              overflow: 'visible',
              maxWidth: '100%'
            }}>
              
              {/* Row 1: Medicine & Transaction Details */}
              <Stack 
                spacing={2} 
                direction="row" 
                flexWrap="wrap" 
                alignItems="center" 
                sx={{ mb: 2, width: '100%' }}
              >
                <TextField
                  label="Medicine Name"
                  value={filterMedicine}
                  onChange={(e) => setFilterMedicine(e.target.value)}
                  size="small"
                  sx={{ minWidth: 180, flexGrow: 1, maxWidth: 220, bgcolor: 'white' }}
                />
                <TextField
                  label="Batch Number"
                  value={filterBatch}
                  onChange={(e) => setFilterBatch(e.target.value)}
                  size="small"
                  sx={{ minWidth: 150, flexGrow: 1, maxWidth: 200, bgcolor: 'white' }}
                />
                <TextField
                  label="Manufacturer"
                  value={filterManufacturer}
                  onChange={(e) => setFilterManufacturer(e.target.value)}
                  select
                  size="small"
                  sx={{ minWidth: 180, flexGrow: 1, maxWidth: 220, bgcolor: 'white' }}
                >
                  <MenuItem value="">All</MenuItem>
                  {[...new Set(transactions.map(t => t.manufacturer).filter(Boolean))].map((m) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Medicine Type"
                  value={filterMedicineType}
                  onChange={(e) => setFilterMedicineType(e.target.value)}
                  select
                  size="small"
                  sx={{ minWidth: 180, flexGrow: 1, maxWidth: 220, bgcolor: 'white' }}
                >
                  <MenuItem value="">All</MenuItem>
                  {[...new Set(transactions.map(t => t.medicineType).filter(Boolean))].map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </TextField>
              </Stack>

              {/* Row 2: Patient & Doctor Details */}
              <Stack 
                spacing={2} 
                direction="row" 
                flexWrap="wrap" 
                alignItems="center" 
                sx={{ mb: 2, width: '100%' }}
              >
                <TextField
                  label="Patient Name"
                  value={filterPatient}
                  onChange={(e) => setFilterPatient(e.target.value)}
                  size="small"
                  sx={{ minWidth: 180, flexGrow: 1, maxWidth: 220, bgcolor: 'white' }}
                />
                <TextField
                  label="Patient Type"
                  value={filterPatientType}
                  onChange={(e) => setFilterPatientType(e.target.value)}
                  select
                  size="small"
                  sx={{ minWidth: 150, flexGrow: 1, maxWidth: 180, bgcolor: 'white' }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="opd">OPD</MenuItem>
                  <MenuItem value="ipd">IPD</MenuItem>
                </TextField>
                <TextField
                  label="Doctor Name"
                  value={filterDoctor}
                  onChange={(e) => setFilterDoctor(e.target.value)}
                  size="small"
                  sx={{ minWidth: 180, flexGrow: 1, maxWidth: 220, bgcolor: 'white' }}
                />
                <TextField
                  label="Supplier Name"
                  value={filterSupplier}
                  onChange={(e) => setFilterSupplier(e.target.value)}
                  select
                  size="small"
                  sx={{ minWidth: 180, flexGrow: 1, maxWidth: 220, bgcolor: 'white' }}
                >
                  <MenuItem value="">All</MenuItem>
                  {[
                    ...new Set(
                      transactions
                        .map((t) => getTransactionSupplierName(t))
                        .filter(Boolean)
                    ),
                  ]
                    .sort((a, b) => String(a).localeCompare(String(b)))
                    .map((name) => (
                      <MenuItem key={name} value={name}>
                        {name}
                      </MenuItem>
                    ))}
                </TextField>
                <TextField
                  label="Transaction Type"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  select
                  size="small"
                  sx={{ minWidth: 150, flexGrow: 1, maxWidth: 180, bgcolor: 'white' }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="IN">IN</MenuItem>
                  <MenuItem value="OUT">OUT</MenuItem>
                  <MenuItem value="RETURN">RETURN</MenuItem>
                </TextField>
              </Stack>

              {/* Row 3: Date Range & Actions */}
              <Stack 
                spacing={2} 
                direction="row" 
                flexWrap="wrap" 
                alignItems="center"
                sx={{ width: '100%' }}
              >
                <TextField
                  label="From date"
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ minWidth: 180, flexGrow: 1, maxWidth: 220, bgcolor: 'white' }}
                />
                <TextField
                  label="To date"
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ minWidth: 180, flexGrow: 1, maxWidth: 220, bgcolor: 'white' }}
                />
                <Tooltip title="Clear all filters">
                  <IconButton 
                    onClick={clearFilters}
                    sx={{ 
                      bgcolor: 'white', 
                      '&:hover': { bgcolor: '#e0e0e0' },
                      border: '1px solid #ddd'
                    }}
                  >
                    <ClearAllIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          )}
        </Box>

        <TableContainer sx={{ 
          flex: 1,
          overflowX: 'auto', 
          overflowY: 'auto',
          minHeight: 0
        }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth || 100 }}
                    sx={{ 
                      backgroundColor: '#568af7', 
                      color: '#fff',
                      fontWeight: 600,
                      // fontSize: 10,
                      position: 'sticky',
                      top: 0,
                      zIndex: 100
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 2 }}>
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : filteredTransactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 2 }}>
                    <Alert severity="info">No transaction history found</Alert>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTransactions
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((transaction) => (
                    <TableRow key={transaction.id} hover>
                      {/* <TableCell>{transaction.id}</TableCell> */}
                     
                      {/* <TableCell>{transaction.medicineInventoryId || "N/A"}</TableCell> */}
                       <TableCell>
                        {transaction.medicineName ? (
                          <Box
                            component="span"
                            sx={clickableIdSx}
                            onClick={() => handleMedicineNameClick(transaction)}
                            title="View inventory batch info"
                          >
                            {transaction.medicineName}
                          </Box>
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                      <TableCell>{transaction.batchNumber}</TableCell>
                      {/* <TableCell>{transaction.manufacturer}</TableCell> */}
                      <TableCell>{transaction.medicineType}</TableCell>
                      {/* <TableCell>{transaction.initialQuantity}</TableCell> */}
                      <TableCell>
                        {transaction.transactionType === "IN" && transaction.deductedQuantity < 0 
                          ? Math.abs(transaction.deductedQuantity) 
                          : transaction.deductedQuantity}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={transaction.transactionType}
                          color={
                            transaction.transactionType === "IN" ? "success" : 
                            transaction.transactionType === "OUT" ? "error" : 
                            transaction.transactionType === "RETURN" ? "warning" : "default"
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {transaction.transactionDate ? dayjs(transaction.transactionDate).format("D MMM YYYY hh:mm A") : "N/A"}
                      </TableCell>
                      <TableCell>{transaction.patientName}</TableCell>
                      {/* <TableCell>
                        <Chip
                          label={transaction.patientType || "N/A"}
                          color={transaction.patientType === "ipd" ? "primary" : transaction.patientType === "opd" ? "secondary" : "default"}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell> */}
                      <TableCell>{transaction.doctorName || "N/A"}</TableCell>
                      <TableCell>
                        {getTransactionSupplierName(transaction) || "N/A"}
                      </TableCell>
                      <TableCell>
                        {getTransactionInvoiceId(transaction) ? (
                          <Box
                            component="span"
                            sx={clickableIdSx}
                            onClick={() => handleInvoiceIdClick(transaction)}
                            title="Open invoice"
                          >
                            {getTransactionInvoiceId(transaction)}
                          </Box>
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                      <TableCell>
                        {getTransactionPrescriptionId(transaction) ? (
                          <Box
                            component="span"
                            sx={clickableIdSx}
                            onClick={() => handlePrescriptionIdClick(transaction)}
                            title="Open prescription"
                          >
                            {getTransactionPrescriptionId(transaction)}
                          </Box>
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                      <TableCell>{transaction.costPerUnit}</TableCell>
                      {/* <TableCell>{transaction.totalCost}</TableCell>  */}
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>


        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={filteredTransactions.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ flexShrink: 0, borderTop: '1px solid #e0e0e0' }}
        />
    </Box>
  );
  console.log(filteredTransactions);
  
};

export default MedicineTransactionHistory;
